import React, { useEffect, useMemo, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { ShieldCheck, ChevronDown, ChevronRight, Paperclip, Plus, Loader2, DollarSign } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/format";
import { CLAIM_STATUS_FLOW, CLAIM_STATUS_META, REASON_LABEL } from "@/lib/claims";
import FileClaimDialog from "@/components/FileClaimDialog";

function ClaimStatusBadge({ status }) {
  const meta = CLAIM_STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return (
    <span data-testid={`claim-status-${status}`} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function StatusTimeline({ current, history }) {
  const denied = current === "denied";
  if (denied) {
    return (
      <div className="text-xs text-red-700 font-semibold">This claim was denied.</div>
    );
  }
  const currentIdx = CLAIM_STATUS_FLOW.indexOf(current);
  return (
    <ol className="flex flex-wrap items-center gap-y-2" data-testid="claim-timeline">
      {CLAIM_STATUS_FLOW.map((step, i) => {
        const done = i <= currentIdx;
        const entry = (history || []).find((h) => h.status === step);
        return (
          <li key={step} className="flex items-center">
            <div className="flex flex-col items-center text-center w-24">
              <div className={`w-3.5 h-3.5 rounded-full border-2 ${done ? "bg-blue-600 border-blue-600" : "bg-white border-slate-300"}`} />
              <span className={`text-[10px] mt-1 leading-tight ${done ? "text-slate-800 font-semibold" : "text-slate-400"}`}>
                {CLAIM_STATUS_META[step].label}
              </span>
              {entry && <span className="text-[9px] text-slate-400">{formatDate(entry.at)}</span>}
            </div>
            {i < CLAIM_STATUS_FLOW.length - 1 && (
              <div className={`h-0.5 w-6 -mt-4 ${i < currentIdx ? "bg-blue-600" : "bg-slate-200"}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function ClaimRow({ claim }) {
  const [open, setOpen] = useState(false);
  const noteEntries = useMemo(
    () => (claim.status_history || []).filter((h) => h.note),
    [claim.status_history]
  );
  return (
    <div data-testid={`claim-row-${claim.id}`} className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`claim-toggle-${claim.id}`}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm">{claim.order_number}</span>
            <span className="text-slate-400">·</span>
            <span className="text-sm text-slate-600 capitalize">{REASON_LABEL(claim.reason)}</span>
          </div>
          <div className="text-xs text-slate-400 mt-0.5">Filed {formatDate(claim.created_at)}</div>
        </div>
        {claim.credit_amount > 0 && (
          <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-0.5">
            <DollarSign className="w-3 h-3" />{claim.credit_amount.toFixed(2)}
          </span>
        )}
        <ClaimStatusBadge status={claim.status} />
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-4 bg-slate-50/50 border-t border-slate-100" data-testid={`claim-detail-${claim.id}`}>
          <div className="pt-3">
            <StatusTimeline current={claim.status} history={claim.status_history} />
          </div>

          {claim.description && (
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-1">Description</p>
              <p className="text-sm text-slate-700">{claim.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Meta label="Wholesale" value={`$${(claim.wholesale_value || 0).toFixed(2)}`} />
            <Meta label="Sold value" value={`$${(claim.sold_value || 0).toFixed(2)}`} />
            {claim.carrier && <Meta label="Carrier" value={claim.carrier} />}
            {claim.tracking_number && <Meta label="Tracking" value={claim.tracking_number} />}
          </div>

          {claim.attachments?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Attachments</p>
              <div className="flex flex-wrap gap-2">
                {claim.attachments.map((a) => (
                  <a
                    key={a.id}
                    href={`${API_BASE}/claims/files/${a.storage_path}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`claim-attachment-link-${a.id}`}
                    className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 hover:border-blue-400 hover:text-blue-600 text-slate-700 rounded-md px-2.5 py-1.5 font-medium transition-colors"
                  >
                    <Paperclip className="w-3.5 h-3.5" /> {a.original_filename}
                  </a>
                ))}
              </div>
            </div>
          )}

          {noteEntries.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Updates</p>
              <ul className="space-y-1.5">
                {noteEntries.map((h, i) => (
                  <li key={`${h.at}-${i}`} className="text-xs text-slate-600">
                    <span className="text-slate-400">{formatDateTime(h.at)}</span> — {h.note}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-800 truncate">{value}</p>
    </div>
  );
}

export default function ClaimsCard({ param, accountId, allMode, reloadKey }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filing, setFiling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const qs = param ? `?${param}` : "";
      const { data } = await api.get(`/claims${qs}`);
      setClaims(data.claims || []);
    } catch {
      setClaims([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [param, reloadKey]);

  if (allMode) return null;

  return (
    <div data-testid="claims-card" className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Claims</h2>
            <p className="text-xs text-slate-500">File and track claims for lost or damaged shipments.</p>
          </div>
        </div>
        <button
          data-testid="new-claim-btn"
          onClick={() => setFiling(true)}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md transition-colors text-sm"
        >
          <Plus className="w-4 h-4" /> File a claim
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-slate-400 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading claims…</div>
      ) : claims.length === 0 ? (
        <p className="text-center text-sm text-slate-400 py-10">No claims yet. If a shipment is lost or damaged, file a claim above.</p>
      ) : (
        <div className="space-y-3" data-testid="claims-list">
          {claims.map((c) => <ClaimRow key={c.id} claim={c} />)}
        </div>
      )}

      {filing && (
        <FileClaimDialog
          accountId={accountId}
          onClose={() => setFiling(false)}
          onSubmitted={() => { setFiling(false); load(); }}
        />
      )}
    </div>
  );
}
